import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material"
import { blue, red, yellow } from "@mui/material/colors"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { alertError, alertSuccess, alertConfirmDelete } from "components/Alert"
import { DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { addIdForDataGrid } from "utils"
import { pcItmMatchColumns, notIsCmptColumns } from "./columns"
import { LinkRounded, DeleteRounded } from "resource/muiImport"
import PettyCashDialog from "../PettyCash/PettyCashDialog"
import FullScreenImageDialog from "components/FullScreenImageDialog/FullScreenImageDialog"

// recompute amount-before-vat / pay-amount and wht defaults when a cell is edited
const recalcRow = (row, field, value) => {
  const updated = { ...row, [field]: value }
  const vatPrct = Number(updated.VatPrct || 0)
  const whtPrct = Number(updated.WhtPrct || 0)
  let amnt = Number(updated.Amnt || 0)
  let amntB4Vat = amnt / (1 + vatPrct / 100)
  if (field === "Amnt") {
    amnt = Number(value || 0)
    amntB4Vat = amnt / (1 + vatPrct / 100)
  } else if (field === "AmntB4Vat") {
    amntB4Vat = Number(value || 0)
    amnt = amntB4Vat * (1 + vatPrct / 100)
  }
  let whtPayrTyp = updated.WhtPayrTyp
  let whtTypId = updated.WhtTypId
  if (field === "WhtPrct") {
    if (Number(value) > 0) {
      if (!whtPayrTyp) whtPayrTyp = "A"
      if (!whtTypId) whtTypId = Number(value) === 1 ? 1 : 5
    } else if (Number(value) === 0) {
      whtPayrTyp = null
      whtTypId = null
    }
  }
  const payAmnt = ((amnt / (1 + vatPrct / 100)) * (1 - whtPrct / 100))
    + (((amnt / (1 + vatPrct / 100)) * vatPrct) / 100)
  return { ...updated, Amnt: amnt, AmntB4Vat: amntB4Vat, PayAmnt: payAmnt, WhtPayrTyp: whtPayrTyp, WhtTypId: whtTypId }
}

// receipt branch/tax id differs from the job's registered branch/tax id
const normBrch = (v) => Number(String(v ?? "").trim()) || 0
const normTax = (v) => String(v ?? "").trim()
const isRcptJobMismatch = (row) => {
  if (row.ExpId !== 3 && row.ExpId !== 5) return false
  const hasJob = normTax(row.JobRcptTaxId) !== "" || row.JobRcptBrchNo != null
  if (!hasJob) return false
  return normTax(row.RcptTaxId) !== normTax(row.JobRcptTaxId)
    || normBrch(row.RcptBrchNo) !== normBrch(row.JobRcptBrchNo)
}

// ids of the pc_items currently being dragged from the left table
let draggedPCItmIds = []

// pc_item currently having its JobOrdId edited via the search popover
let selectedRowPCItmId = null

let selectedPCTrnsId = null
const MatchAdvanceExpenseDialog = ({ dialogOpen, setDialogOpen, selectedDrvId, onFinish, pcTrnsId: scopePCTrnsId = null, isOffice = false }) => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [leftRows, setLeftRows] = useState([])
  const [rightRows, setRightRows] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [pcJnlId, setPcJnlId] = useState(null)
  const [dialogPettyCashOpen, setDialogPettyCashOpen] = useState(false)
  const [imgDialogOpen, setImgDialogOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState("")
  // JobOrdId highlighted across both tables (click a JobOrdId cell to toggle)
  const [highlightJobOrdId, setHighlightJobOrdId] = useState(null)

  const handleImageClick = useCallback((url) => {
    setImgUrl(url)
    setImgDialogOpen(true)
  }, [])

  const selectionModelRef = useRef([])
  selectionModelRef.current = selectionModel

  // office mode lists all office-user uploads (AccTypId=1); driver mode lists one driver's uploads
  const getArgs = useMemo(() => (
    isOffice
      ? { OnlyOffice: 1, OnlyUnlinked: 1, WithNoIsCmpt: 1, NoIsCmptPCTrnsId: scopePCTrnsId }
      : { DrvId: selectedDrvId, OnlyUnlinked: 1, WithNoIsCmpt: 1, NoIsCmptPCTrnsId: scopePCTrnsId }
  ), [isOffice, selectedDrvId, scopePCTrnsId])

  // single call returns both the unlinked receipts (left) and the not-complete
  // pc_transactions (right) via WithNoIsCmpt. When pcTrnsId is given (opened from
  // PettyCashDialog), the right table is scoped to that single transaction.
  const getData = useCallback(() => {
    if (!isOffice && !selectedDrvId) return
    ax.post("/petty-cash/getPCItemFileDriverTable", getArgs).then(value => {
      if (value.data) {
        setLeftRows(addIdForDataGrid(value.data.pcItems, "PCItmId"))
        const noIsCmpt = value.data.noIsCmpt
        setRightRows(addIdForDataGrid(noIsCmpt, "PCTrnsId"))
        // default the highlight to the first bottom-table row's JobOrdId
        setHighlightJobOrdId(noIsCmpt?.[0]?.JobOrdId ?? null)
      }
    })
  }, [ax, isOffice, selectedDrvId, getArgs])

  useEffect(() => {
    if (dialogOpen && (selectedDrvId || isOffice)) {
      setSelectionModel([])
      setHighlightJobOrdId(null)
      setPcJnlId(msData.pcJournalCombo?.find(item => item.IsDf)?.id || null)
      getData()
    }
  }, [dialogOpen, selectedDrvId, isOffice, getData, msData.pcJournalCombo])

  const handleRowDragStart = useCallback((e) => {
    const id = Number(e.currentTarget.getAttribute("data-id"))
    const selected = selectionModelRef.current
    draggedPCItmIds = (selected.length > 0 && selected.includes(id))
      ? selected.map(Number)
      : [id]
    e.dataTransfer.effectAllowed = "move"
  }, [])

  const handleRowDragOver = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.classList.add("drop-target")
  }, [])

  const handleRowDragLeave = useCallback((e) => {
    e.currentTarget.classList.remove("drop-target")
  }, [])

  const handleRowDrop = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.classList.remove("drop-target")
    const pcTrnsId = Number(e.currentTarget.getAttribute("data-id"))
    if (!pcTrnsId || draggedPCItmIds.length === 0) return
    const draggedRows = leftRows.filter(r => draggedPCItmIds.includes(r.PCItmId))
    if (draggedRows.some(r => !r.ExpId)) {
      alertError("ใบเสร็จที่ลากมาบางรายการไม่มีประเภทค่าใช้จ่าย กรุณาระบุก่อนจับคู่")
      draggedPCItmIds = []
      return
    }
    const postData = {
      PCItmIds: draggedPCItmIds,
      PCTrnsId: pcTrnsId,
      getFn: "getPCItemFileDriverTable",
      getArgs,
    }
    ax.post("/petty-cash/updatePCItemPCTrns", postData).then(value => {
      if (value.data) {
        alertSuccess("ผูกใบเสร็จกับรายการเบิกเงินเรียบร้อยแล้ว")
        draggedPCItmIds = []
        setSelectionModel([])
        setLeftRows(addIdForDataGrid(value.data.pcItems, "PCItmId"))
        setRightRows(addIdForDataGrid(value.data.noIsCmpt, "PCTrnsId"))
        if (onFinish) onFinish()
      }
    })
  }, [ax, leftRows, onFinish, getArgs])

  const handlePopoverSearchOpen = useCallback((id) => () => {
    selectedRowPCItmId = id
  }, [])

  // pick a job order for the receipt row: persist JobOrdId and reflect the job's cols in the grid
  const handleSelectJobOrder = useCallback((item) => {
    if (!selectedRowPCItmId) return
    ax.post("/petty-cash/updatePCItemCols", {
      PCItmId: selectedRowPCItmId,
      ColValues: { JobOrdId: item.JobOrdId },
    }, false).then(res => {
      if (res.data) {
        setLeftRows(o => {
          const result = [...o]
          const idx = result.findIndex(r => r.id === selectedRowPCItmId)
          if (idx !== -1) {
            result[idx] = {
              ...result[idx],
              JobOrdId: item.JobOrdId,
              JobSNm: item.JobSNm ?? result[idx].JobSNm,
              ContNo: item.ContNo ?? result[idx].ContNo,
              JobNo: item.JobNo ?? result[idx].JobNo,
            }
          }
          return result
        })
      }
    })
  }, [ax])

  const leftColumns = useMemo(() => pcItmMatchColumns({
    expCombo: msData.expOprCombo,
    whtTypeCombo: msData.whtTypeCombo,
    supplierCombo: msData.supplierCombo,
    onImageClick: handleImageClick,
    handlePopoverSearchOpen,
    handleSelectJobOrder,
    suggestDrvId: isOffice ? null : selectedDrvId,
  }), [msData.expOprCombo, msData.whtTypeCombo, msData.supplierCombo, handleImageClick
    , handlePopoverSearchOpen, handleSelectJobOrder, isOffice, selectedDrvId])

  const handleLeftEditCommit = useCallback((params) => {
    // JobOrdId is a plain id column: clear when blank, otherwise validate the job exists before persisting
    if (params.field === "JobOrdId") {
      const { id, value } = params
      if (value === null || value === "") {
        ax.post("/petty-cash/updatePCItemCols", {
          PCItmId: id,
          ColValues: { JobOrdId: null },
        }, false).then(res => {
          if (res.data) {
            setLeftRows(o => o.map(r => r.id === id ? { ...r, JobOrdId: null } : r))
          }
        })
        return
      }
      ax.post("/petty-cash/getJobOrderLocation", { JobOrdId: value }, false).then(res => {
        if (!res.data || res.data.length !== 1) {
          alertError("ไม่พบข้อมูล เลขที่ใบงานนี้ (ข้อมูลยังไม่ถูกบันทึก)")
          return
        }
        ax.post("/petty-cash/updatePCItemCols", {
          PCItmId: id,
          ColValues: { JobOrdId: value },
        }, false).then(res2 => {
          if (res2.data) {
            setLeftRows(o => o.map(r => r.id === id ? { ...r, JobOrdId: value } : r))
          }
        })
      })
      return
    }
    setLeftRows(o => {
      const result = [...o]
      const idx = result.findIndex(r => r.id === params.id)
      if (idx === -1) return result
      const newRow = recalcRow(result[idx], params.field, params.value)
      result[idx] = newRow
      // persist the edited pc_item columns (no calDiffAmntAndUpdate - receipt has no PayAmnt-based transaction)
      ax.post("/petty-cash/updatePCItemCols", {
        PCItmId: newRow.PCItmId,
        ColValues: {
          ExpId: newRow.ExpId ?? null,
          Amnt: newRow.Amnt ?? 0,
          VatPrct: newRow.VatPrct ?? 0,
          WhtPrct: newRow.WhtPrct ?? 0,
          WhtPayrTyp: newRow.WhtPayrTyp ?? null,
          WhtTypId: newRow.WhtTypId ?? null,
          SupId: newRow.SupId ?? null,
        },
      }, false)
      return result
    })
  }, [ax])

  const handleReimburse = useCallback(() => {
    if (selectionModel.length === 0) {
      alertError("กรุณาเลือกใบเสร็จอย่างน้อย 1 รายการ")
      return
    }
    if (!pcJnlId) {
      alertError("กรุณาเลือกสมุดเงินสดย่อย")
      return
    }
    const selectedRows = leftRows.filter(row => selectionModel.includes(row.id))
    if (selectedRows.some(row => !row.ExpId)) {
      alertError("ใบเสร็จที่เลือกบางรายการไม่มีประเภทค่าใช้จ่าย ไม่สามารถทำเบิกได้")
      return
    }
    const amnt = selectedRows.reduce((sum, row) => sum + Number(row.Amnt || 0), 0)
    const expId = selectedRows[0].ExpId
    const jobOrdId = selectedRows[0].JobOrdId
    const postData = {
      PCItmIds: selectionModel.map(Number),
      Amnt: amnt,
      ExpId: expId,
      PCJnlId: pcJnlId,
      DrvId: selectedDrvId,
      JobOrdId: jobOrdId,
    }
    ax.post("/petty-cash/reimbursePCItemFromReceipt", postData).then(value => {
      if (value.data) {
        alertSuccess("สร้างรายการเบิกเงินเรียบร้อยแล้ว")
        setSelectionModel([])
        getData()
        if (onFinish) onFinish()
      }
    })
  }, [ax, selectionModel, pcJnlId, leftRows, selectedDrvId, getData, onFinish])

  const handleDelete = useCallback(() => {
    if (selectionModel.length === 0) return
    alertConfirmDelete(`ต้องการลบใบเสร็จ ${selectionModel.length} รายการ หรือไม่`, () => {
      ax.post("/petty-cash/deleteDriverReceiptList", { PCItmIds: selectionModel.map(Number) }).then(value => {
        if (value.data) {
          setSelectionModel([])
          getData()
          if (onFinish) onFinish()
        }
      })
    })
  }, [ax, selectionModel, getData, onFinish])

  const drvNm = useMemo(() =>
    isOffice ? "Office" : (msData.driverCombo?.find(item => item.id === selectedDrvId)?.label || "")
    , [isOffice, msData.driverCombo, selectedDrvId])

  // toggle the highlighted JobOrdId; clicking the same one again clears it
  const toggleHighlightJobOrdId = useCallback((jobOrdId) => {
    setHighlightJobOrdId(prev => (jobOrdId != null && prev === jobOrdId) ? null : (jobOrdId ?? null))
  }, [])

  const handleLeftCellClick = useCallback(({ field, row }) => {
    if (field === "JobOrdId") toggleHighlightJobOrdId(row.JobOrdId)
  }, [toggleHighlightJobOrdId])

  const handlePCTrnsCellClick = useCallback(({ field, id, row }) => {
    if (field === "ShowDetail") {
      selectedPCTrnsId = id
      setDialogPettyCashOpen(true)
    } else if (field === "JobOrdId") {
      toggleHighlightJobOrdId(row.JobOrdId)
    }
  }, [toggleHighlightJobOrdId])

  const handlePettyCashDialogFinish = useCallback(() => {
    getData()
  }, [getData])
  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperProps={{
        style: {
          maxWidth: 2150
        }
      }}
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`จับคู่ค่าใช้จ่ายล่วงหน้า - ${drvNm}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC height="75vh" pt={1} sx={{ gap: 1 }}>
          <BoxFR sx={{ gap: 1 }}>
            <ComboBox options={msData.pcJournalCombo} label="สมุดเงินสดย่อย" sx={{ width: 200 }}
              selectedId={pcJnlId}
              setSelectedId={id => setPcJnlId(id)}
            />
            <Button variant="contained" color="primary" onClick={handleReimburse}>
              <LinkRounded sx={{ mr: 1 }} /> สร้างการเบิกจากใบเสร็จที่เลือก
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}
              disabled={selectionModel.length === 0}>
              <DeleteRounded sx={{ mr: 1 }} /> ลบ ({selectionModel.length})
            </Button>
            <Box flex={1} />
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
              ลากใบเสร็จจากตารางบน ไปวางที่รายการเบิกเงินในตารางล่าง
            </Typography>
          </BoxFR>
          <BoxFC height="100%" sx={{ gap: 1, alignItems: "stretch" }}>
            <BoxFC flex={1} sx={{ gap: 0.5 }} >
              <Typography fontWeight="bold" sx={{ pl: 1 }}>ใบเสร็จที่ยังไม่ได้ผูก</Typography>
              <Box height="100%" sx={{
                "& .MuiDataGrid-row.rcpt-job-mismatch": {
                  bgcolor: red[100],
                  "&:hover": { bgcolor: red[100] },
                },
                "& .MuiDataGrid-cell.job-highlight": {
                  bgcolor: yellow[100],
                },
              }}>
                <DataGridCellExpand
                  hideFooter
                  checkboxSelection
                  disableSelectionOnClick
                  selectionModel={selectionModel}
                  onSelectionModelChange={setSelectionModel}
                  rows={leftRows}
                  columns={leftColumns}
                  onCellClick={handleLeftCellClick}
                  onCellEditCommit={handleLeftEditCommit}
                  getRowClassName={({ row }) => isRcptJobMismatch(row) ? "rcpt-job-mismatch" : ""}
                  getCellClassName={({ field, row }) =>
                    field === "JobOrdId" && highlightJobOrdId != null && row.JobOrdId === highlightJobOrdId
                      ? "job-highlight" : ""}
                  componentsProps={{
                    row: { draggable: true, onDragStart: handleRowDragStart },
                  }}
                />
              </Box>
            </BoxFC>
            <BoxFC flex={1} sx={{ gap: 0.5 }} >
              <Typography fontWeight="bold" sx={{ pl: 1 }}>รายการเบิกเงินที่ยังไม่ครบ</Typography>
              <Box height="100%" sx={{
                "& .MuiDataGrid-row.drop-target": {
                  bgcolor: blue[100],
                  outline: `2px dashed ${blue[400]}`,
                  outlineOffset: -2,
                },
                "& .MuiDataGrid-cell.job-highlight": {
                  bgcolor: yellow[100],
                },
              }}>
                <DataGridCellExpand
                  hideFooter
                  density="standard"
                  rows={rightRows}
                  columns={notIsCmptColumns}
                  onCellClick={handlePCTrnsCellClick}
                  getCellClassName={({ field, row }) =>
                    field === "JobOrdId" && highlightJobOrdId != null && row.JobOrdId === highlightJobOrdId
                      ? "job-highlight" : ""}
                  componentsProps={{
                    row: {
                      onDragOver: handleRowDragOver,
                      onDragLeave: handleRowDragLeave,
                      onDrop: handleRowDrop,
                    },
                  }}
                />
              </Box>
            </BoxFC>
          </BoxFC>
          <PettyCashDialog
            selectedId={selectedPCTrnsId}
            dialogOpen={dialogPettyCashOpen}
            setDialogOpen={setDialogPettyCashOpen}
            onFinish={handlePettyCashDialogFinish}/>
          <FullScreenImageDialog
            linkUrl={imgUrl}
            dialogOpen={imgDialogOpen}
            setDialogOpen={setImgDialogOpen}
          />
        </BoxFC>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(MatchAdvanceExpenseDialog)
